import React from 'react'
import Helmet from 'react-helmet'

// Components
import { useForTablet } from '@sketch/components'
import Footer from 'components/Footer'
import Navbar from 'components/Navbar'
import { SplitLayout } from 'layouts/SplitLayout'

import { Sidebar, NoWorkspaceSidebar } from '../Sidebar'

import {
  SelectorSection,
  HamburgerButton,
  HamburgerIcon,
  SidebarWrapper,
  Margin,
} from './WorkspaceSidebarLayout.styles'

import { WorkspaceMinimalFragment } from '@sketch/gql-types'

interface WorkspaceSidebarLayoutProps {
  title: string
  currentWorkspace: WorkspaceMinimalFragment | undefined
  workspaces: WorkspaceMinimalFragment[]
  navbar?: React.ReactNode
  subheader?: React.ReactNode
  hideFooter?: boolean
}

const WorkspaceSidebarLayout: React.FC<WorkspaceSidebarLayoutProps> = props => {
  const {
    children,
    title,
    workspaces = [],
    currentWorkspace,
    navbar,
    hideFooter,
    subheader,
  } = props

  const isTabletAndBigger = useForTablet()
  const isSmallerThanTablet = !isTabletAndBigger

  /**
   * In some views there is no need to have current workspace.
   * E.g. user settings, updates, discovery page and so on.
   *
   * So if there is no selected workspace, just pick first one from the list.
   * (if that list contains any items).
   */
  const workspace: WorkspaceMinimalFragment | undefined =
    currentWorkspace || workspaces?.[0]

  const sidebar = (
    <SidebarWrapper>
      {!workspace || workspaces.length === 0 ? (
        <NoWorkspaceSidebar />
      ) : (
        <Sidebar workspace={workspace} />
      )}
    </SidebarWrapper>
  )

  return (
    <>
      <Helmet>
        <meta name="theme-color" content="#F0F0F0" />
      </Helmet>
      <SplitLayout
        title={title}
        header={({ setSidebarLeftOpen, isSidebarLeftOpen }) => (
          <Navbar
            isSidebarLeftOpen={isSidebarLeftOpen}
            singleContent={!navbar && !isSmallerThanTablet}
          >
            {isSmallerThanTablet && (
              <SelectorSection>
                <HamburgerButton
                  aria-label="Hamburger menu button"
                  onClick={() => setSidebarLeftOpen?.(true)}
                >
                  <HamburgerIcon />
                </HamburgerButton>
              </SelectorSection>
            )}
            {navbar}
          </Navbar>
        )}
        footer={!hideFooter && <Footer />}
        sidebar={sidebar}
      >
        {subheader ? subheader : null}
        <Margin>{children}</Margin>
      </SplitLayout>
    </>
  )
}

export default WorkspaceSidebarLayout
